import React, { useCallback, useContext, useEffect } from "react";
import { FormControlLabel, Radio, RadioGroup, TableCell, TableRow } from "@mui/material";
import { ProformaEmployeeCostReportSectionRef } from "../../../../models/proforma-employee-cost-report/proforma-employee-cost-report-section-ref";
import { SELECTED_PERIOD, SELECTED_PERIOD_DIVIDER } from "../../../../utils/constants/employee-cost-report-constants";
import { ProformaEmployeeCostReportContext } from "../ProformaEmployeeCostReport";
import ContributingSections from "./ContributingSections";

interface Props {
  selectedPeriod: SELECTED_PERIOD;
  section: ProformaEmployeeCostReportSectionRef;
  handleChangeSelectedPeriod: (selectedPeriod: SELECTED_PERIOD) => void;
  contributingSections: ProformaEmployeeCostReportSectionRef[];
  handleUpdateSection: (updatedSection: ProformaEmployeeCostReportSectionRef) => void;
}
const EmployeeCostPriceToTheClientSection = ({
  selectedPeriod,
  section,
  handleUpdateSection,
  handleChangeSelectedPeriod,
  contributingSections,
}: Props) => {
  const { viewMode } = useContext(ProformaEmployeeCostReportContext);

  const calculateYearlyAmount = useCallback(() => {
    if (contributingSections.length > 0) {
      const amountsAdded: number[] = contributingSections
        .filter((currentSection) => !currentSection.type?.isSubstracted)
        .map((currentSection) => currentSection.amount);

      const totalAmountsAdded = amountsAdded.length > 0 ? amountsAdded.reduce((a: number, b: number) => a + b) : 0;

      const amountsSubstracted = contributingSections
        .filter((currentSection) => currentSection.type?.isSubstracted)
        .map((currentSection) => currentSection.amount);

      const totalAmountsSubstracted = amountsSubstracted.length > 0 ? amountsSubstracted.reduce((a, b) => a + b) : 0;

      if (section.amount !== totalAmountsAdded - totalAmountsSubstracted) {
        handleUpdateSection({
          ...section,
          amount: totalAmountsAdded - totalAmountsSubstracted,
        });
      }
    }
  }, [contributingSections, section, handleUpdateSection]);

  useEffect(() => {
    calculateYearlyAmount();
  }, [contributingSections]);

  return (
    <>
      <TableRow>
        <TableCell
          rowSpan={4}
          style={{
            verticalAlign: "text-top",
            fontWeight: "bold",
          }}
        >
          {section.type?.name}
        </TableCell>
        <TableCell rowSpan={4}>
          <RadioGroup
            value={selectedPeriod}
            onChange={(event) => handleChangeSelectedPeriod(event.target.value as SELECTED_PERIOD)}
          >
            <FormControlLabel
              value={SELECTED_PERIOD.yearly}
              control={
                <Radio
                  size="small"
                  disabled={viewMode}
                />
              }
              label={"Yearly"}
            />
            <FormControlLabel
              value={SELECTED_PERIOD.monthly}
              control={
                <Radio
                  size="small"
                  disabled={viewMode}
                />
              }
              label={"Monthly"}
            />
            <FormControlLabel
              value={SELECTED_PERIOD.daily}
              control={
                <Radio
                  size="small"
                  disabled={viewMode}
                />
              }
              label={"Daily"}
            />
            <FormControlLabel
              value={SELECTED_PERIOD.hourly}
              control={
                <Radio
                  size="small"
                  disabled={viewMode}
                />
              }
              label={"Hourly"}
            />
          </RadioGroup>
        </TableCell>
        <TableCell align="right">
          <ContributingSections
            sectionName={section.type?.name ?? ""}
            calculatedAmount={section.amount ?? 0}
            contributions={contributingSections}
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell align="right">
          <ContributingSections
            sectionName={section.type?.name ?? ""}
            calculatedAmount={section.amount / SELECTED_PERIOD_DIVIDER.monthly}
            contributions={contributingSections}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="right">
          <ContributingSections
            sectionName={section.type?.name ?? ""}
            calculatedAmount={section.amount / SELECTED_PERIOD_DIVIDER.daily}
            contributions={contributingSections}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="right">
          <ContributingSections
            sectionName={section.type?.name ?? ""}
            calculatedAmount={section.amount / SELECTED_PERIOD_DIVIDER.hourly}
            contributions={contributingSections}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export default EmployeeCostPriceToTheClientSection;
